import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, ActivationStart, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, filter, iif, map, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';

import { environment } from 'environments/environment';

import { AccountService, FixRateService, TariffsFacade } from '@personal-area/global/accounts/services';
import { AccountTariff, CreatedAccount, SettingsForm, TradingAccount, ServerType, Tariff, defaultCurrency, defaultServer } from '@personal-area/global/accounts/models';
import { Role } from '@personal-area/global/users/models';
import { UnsubscribeController } from '@personal-area/global/common'
import { AuthService } from '@personal-area/global/authorization/services';
import { UserService, UserFacade } from '@personal-area/global/users/services';
import { RegisterParams } from '@personal-area/global/registration';
import { ApiFormService } from '@personal-area/global/form-validation/services';
import { TypeConverter } from '@personal-area/global/common';

@Component({
    selector: 'pac-account-tariff',
    templateUrl: './account-tariff.component.html',
    styleUrls: ['./account-tariff.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountTariffComponent extends UnsubscribeController implements OnInit {
  @Input() params = new RegisterParams();
  @Input() isAutoSubmit = false;
  @Input() selectedServer: ServerType;

  @Output() accountCreated = new EventEmitter<CreatedAccount>();
  @Output() beforeCreate = new EventEmitter<TradingAccount>();

  public isLoading$ = new BehaviorSubject<boolean>(true);
  public tariff$ = new BehaviorSubject<Tariff>(null);
  public isIslamicCountry = environment.islamicCountries.includes(this._userFacade.loggedUser.country);
  public isTurkish = this._userFacade.loggedUser.country === 'TUR';
  public isSwapFreeAvailable = environment.isSwapFreeAvailable;
  public selectedCurrency: string;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _accountService: AccountService,
    private _formService: ApiFormService,
    private _authService: AuthService,
    private _fixRateService: FixRateService,
    private _tariffsFacade: TariffsFacade,
    private _userFacade: UserFacade,
    private _translateService: TranslateService,
    private _userService: UserService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this._translateService.onLangChange
      .pipe(
        startWith(null),
        tap(() => this._tariffsFacade.getTariffs(this._userFacade.loggedUser.id)),
        switchMap(() => this._tariffsFacade.isLoaded$),
        filter(Boolean),
        switchMap(() => combineLatest([
          this._tariffsFacade.tariffs$,
          this._route.params.pipe(map(params => params['tariff'] as AccountTariff)),
        ])),
        map(([tariffs, tariffName]) => tariffs.find(({ type }) => type === tariffName)),
        withLatestFrom(this._router.events),
        takeUntil(this._unsubscribe),
      )
      .subscribe(([tariff, routerEvent]) => {
        // Navigation to logout might be cancelled due to redirect at `this._processTariff`. This case will exclude tariff processing on logout navigation
        if (routerEvent instanceof ActivationStart && routerEvent?.snapshot.url?.some(segment => segment?.path.includes('logout'))) {
          return;
        }
        this._processTariff(tariff);
      });
  }

  public formSubmit(value: SettingsForm, tariff: Tariff): void {
    const account: TradingAccount = {
      server: value.server,
      tariff: value.tariff,
      leverage: Number(value.leverage),
      role: tariff.defaultRole,
      amount: value.amount ? TypeConverter.moneyToNumber(value.amount.toString()) : 0,
      currency: value.fixRate ? this._fixRateService.getCurrencyByCountry() : value.currency,
      isSwapFreeEnabled: value.isSwapFreeEnabled,
      creationKey: ''
    };

    this.beforeCreate.emit(account);
    this.isLoading$.next(true);

    iif(() => tariff.type === AccountTariff.partner,
      this._accountService.openPartnerAccount(),
      this._accountService.openTraderAccount(this._userFacade.loggedUser.id, account),
    )
      .subscribe({
        next: ({ id }) => this._markUserHasAccounts(Number(id), account),
        error: (err: HttpErrorResponse) => {
          this.isAutoSubmit = false;
          this._formService.defaultFormError(err);
          this.isLoading$.next(false);
        }
      });
  }

  public goBack(): void {
    this._router.navigate(['../'], {
      queryParamsHandling: 'preserve',
      relativeTo: this._route,
    });
  }

  private _processTariff(tariff: Tariff): void {
    if (!tariff || !tariff.availability.isAvailable) {
      this.goBack();
      return;
    }

    this.selectedCurrency = tariff.currencies.includes(this.params.currency)
      ? this.params.currency.toUpperCase()
      : defaultCurrency;
    this.tariff$.next(tariff);

    if (!this.isAutoSubmit) {
      this.isLoading$.next(false);
      return;
    }

    const serversName = tariff.servers.map(({ name }) => name);

    this.formSubmit({
      server: serversName.includes(defaultServer)
        ? defaultServer
        : serversName[0],
      tariff: tariff.type,
      leverage: Math.max(...tariff.leverages),
      currency: this.selectedCurrency,
      amount: 0,
      fixRate: false,
      isSwapFreeEnabled: false,
    }, tariff);
  }

  private _markUserHasAccounts(id: number, { server, tariff }: TradingAccount): void {
    this._userFacade.updateUserOptions({ hasOpenedAccounts: true });

    /// TODO: figure out whether the condition is the same as above one
    if (!this._authService.hasCabinetAccounts) {
      const newRole = tariff === AccountTariff.partner
        ? Role.Partner
        : Role.Trader;

      this._userService.addRoleToAccountTypes(newRole);
    }

    this.accountCreated.emit({ id, tariff, server });
  }
}
